import { useState, useEffect, useRef, useCallback } from 'react';

// Default to localhost:8000 if environment variable is not set
const DEFAULT_BACKEND_URL = 'http://localhost:8000';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || DEFAULT_BACKEND_URL;

const API_URL = BACKEND_URL + '/api';
const WS_URL  = BACKEND_URL
  .replace(/^https/, 'wss').replace(/^http(?!s)/, 'ws') + '/api/ws';

const DEFAULT_STATS  = { total: 22, nationalities: {}, stage: 0 };
const DEFAULT_CONFIG = {
  thresholds: { s1: 5, s2: 20, s3: 100, s4: 500 },
  atmosphere: { particleCount: 50000, curlSpeed: 1.0, bloom: 1.5 }
};

export function useWebSocket() {
  const [stats,     setStats]     = useState(DEFAULT_STATS);
  const [config,    setConfig]    = useState(DEFAULT_CONFIG);
  const [connected, setConnected] = useState(false);
  const wsRef      = useRef(null);
  const retryTimer = useRef(null);

  const applyMsg = useCallback((data) => {
    if (['full_state','update','config_update'].includes(data.type)) {
      setStats({ total: data.total, nationalities: data.nationalities, stage: data.stage });
      if (data.thresholds)  setConfig(p => ({ ...p, thresholds:  data.thresholds  }));
      if (data.atmosphere)  setConfig(p => ({ ...p, atmosphere:  data.atmosphere  }));
    }
  }, []);

  const connect = useCallback(() => {
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;
    ws.onopen  = () => setConnected(true);
    ws.onclose = () => {
      setConnected(false);
      retryTimer.current = setTimeout(connect, 3500);
    };
    ws.onerror = () => ws.close();
    ws.onmessage = (e) => {
      try { applyMsg(JSON.parse(e.data)); } catch(_) {}
    };
  }, [applyMsg]);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(retryTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  const contribute = useCallback(async (nationality, sessionId) => {
    try {
      const res = await fetch(`${API_URL}/contribute`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ nationality, session_id: sessionId }),
      });
      const body = await res.json();
      
      if (!res.ok) return { success: false, error: body.detail || 'Error' };
      
      // We return 'body' so the UI gets the ID and the real total count
      return { success: true, ...body }; 
    } catch {
      return { success: false, error: 'Network error' };
    }
  }, []);

  const adminAction = useCallback(async (path, method = 'POST', body = null) => {
    try {
      const res = await fetch(`${API_URL}${path}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      return await res.json();
    } catch {
      return { success: false };
    }
  }, []);

  return { stats, config, connected, contribute, adminAction };
}
